import {Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {Product} from '../../../models/product';
import {Offer} from '../../../models/offer';
import {Partner} from '../../../models/partner/partner';
import {Country} from '../../../models/country';
import {DateRangeSelectorComponent} from '../date-range-selector/date-range-selector.component';
import {FormsModule} from '@angular/forms';
import { NgOptimizedImage } from '@angular/common';
import {LogFilter} from '../../../types/log-filter';
import {OfferService} from '../../../services/offer/offer.service';
import {forkJoin} from 'rxjs';
import {CountryService} from '../../../services/country/country.service';
import {LangService} from '../../../services/language/lang.service';
import {ProductService} from '../../../services/product/product.service';
import {AuthService} from '../../../services/auth/auth.service';
import {ElementLoaderDirective} from '../../../directives/element-loader/element-loader.directive';
import {DropdownSelectComponent} from '../dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../dropdown-select-option/dropdown-select-option.component';
import {TranslatePipe} from '@ngx-translate/core';
import {PartnerService} from '../../../services/partner/partner.service';
import {DateRange} from '../../../types/date-range';
import {Language} from '../../../models/language';
import {LandingPageService} from '../../../services/landing-page/landing-page.service';
import {Paginator} from '../../../tools/pagination/paginator';
import {LandingPage} from '../../../models/landing-page/landing-page';
import {LandingPageActivityPeriods} from '../../../constants/landing-page-activity';

@Component({
  selector: 'app-log-filters',
  imports: [
    DateRangeSelectorComponent,
    FormsModule,
    ElementLoaderDirective,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    NgOptimizedImage,
    TranslatePipe
],
  templateUrl: './log-filters.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './log-filters.component.scss'
})
export class LogFiltersComponent implements OnInit {

  @Input() filters!: LogFilter;
  @Input() hasLandingPage: boolean = true;
  @Output() init = new EventEmitter<void>();
  @Output() offerChange = new EventEmitter<void>();

  products: Product[] = [];
  offers: Offer[] = [];
  partners: Partner[] = [];
  countries: Country[] = [];
  languages: Language[] = [];
  offerLoading = false;
  partnerLoading = false;
  partnerAccountTypeList: string[] = [];
  landingPages: LandingPage[] = [];
  landingPageLoading = false;
  activityPeriods = LandingPageActivityPeriods;

  constructor(
    protected countryService: CountryService,
    protected langService: LangService,
    private auth: AuthService,
    private offerService: OfferService,
    private productService: ProductService,
    private partnerService: PartnerService,
    private landingPageService: LandingPageService,
  ) {
  }

  ngOnInit(): void {
    forkJoin([
      this.productService.findAll({sort: 'name'}),
      this.countryService.findAll(),
      this.auth.getAsyncConfig(),
      this.langService.getLanguages(),
    ]).subscribe(([products, countries, config, languages]) => {
      this.products = products;
      this.countries = countries;
      this.partnerAccountTypeList = config?.data?.partnerAccountTypeList;
      this.languages = languages;

      // Initial load: keep the offer/partner restored from the URL
      this.refreshOffers(false);
      this.refreshPartners(false);
      this.refreshLandingPages(false);

      this.init.emit();
    });
  }

  updateDate(dateRange: DateRange) {
    this.filters.date.start = dateRange.start;
    this.filters.date.end = dateRange.end;
  }

  changeProduct(): void {
    this.filters['offerId'] = null;
    this.refreshOffers();
    this.refreshLandingPages();
  }

  changeOffer(): void {
    this.refreshLandingPages();
    this.offerChange.emit();
  }

  getProductPlaceholder(): string {
    return this.products.length ? ('All ' + this.products.length + ' product(s)') : 'No product';
  }

  refreshOffers(reset: boolean = true): void {
    this.offerLoading = true;

    const params: any = {};

    if (this.filters['productId']) {
      params.productId = this.filters['productId'];
    }

    this.offerService.findAll(params).subscribe(offers => {
      if (reset) {
        this.filters['offerId'] = null;
      }
      this.offers = offers;
      this.offerLoading = false;
    });
  }

  getOfferPlaceholder(): string {
    return this.offers.length ? ('All ' + this.offers.length + ' offer(s)') : 'No offer';
  }

  getPartnerPlaceholder(): string {
    return this.partners.length ? ('All ' + this.partners.length + ' partner(s)') : 'No partner';
  }

  refreshLandingPages(reset: boolean = true): void {
    if (!this.hasLandingPage) {
      return;
    }

    this.landingPageLoading = true;

    const params: any = {sort: 'name'};

    if (this.filters['offerId']) {
      params.offerId = this.filters['offerId'];
    } else if (this.filters['productId']) {
      params.productId = this.filters['productId'];
    }

    if (this.filters['landingPageActiveSince']) {
      params.activeSince = this.filters['landingPageActiveSince'];
    }

    this.landingPageService.findAll(params, new Paginator(1000)).subscribe(landingPages => {
      if (reset) {
        this.filters['landingPageId'] = null;
      }
      this.landingPages = landingPages;
      this.landingPageLoading = false;
    });
  }

  getLandingPagePlaceholder(): string {
    const active = this.filters['landingPageActiveSince'] ? ' active' : '';

    return this.landingPages.length ? ('All ' + this.landingPages.length + active + ' promotion link(s)') : 'No' + active + ' promotion link';
  }

  refreshPartners(reset: boolean = true): void {
    this.partnerLoading = true;

    const params: any = {
      sort: 'companyName',
      order: 'asc'
    };

    if (this.filters['accountType']) {
      params.accountType = this.filters['accountType'];
    }

    this.partnerService.findAll(params).subscribe((partners: Partner[]) => {
      if (reset) {
        this.filters['partnerId'] = null;
      }
      this.partners = partners;
      this.partnerLoading = false;
    });
  }
}
